import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import {
  CompressOutlined as CompressIcon,
  CloudUpload as UploadIcon,
  Shield as ModerationIcon,
  AutoAwesome as AiIcon,
  Storage as DatabaseIcon,
  Search as IndexIcon,
  CheckCircle as DoneIcon,
  Error as ErrorIcon,
  PhotoSizeSelectLarge as ResizeIcon,
} from '@mui/icons-material';

export type PipelineStepStatus = 'pending' | 'active' | 'done' | 'error' | 'skipped';

export interface PipelineStep {
  id: string;
  label: string;
  icon: React.ReactNode;
  status: PipelineStepStatus;
}

export const UPLOAD_PIPELINE_STEPS: Omit<PipelineStep, 'status'>[] = [
  { id: 'compress',    label: 'Compress',    icon: <CompressIcon fontSize="small" /> },
  { id: 'upload',      label: 'Upload',      icon: <UploadIcon fontSize="small" /> },
  { id: 'moderation',  label: 'Moderation',  icon: <ModerationIcon fontSize="small" /> },
  { id: 'resize',      label: 'Resize',      icon: <ResizeIcon fontSize="small" /> },
  { id: 'ai_analysis', label: 'AI Analysis', icon: <AiIcon fontSize="small" /> },
  { id: 'database',    label: 'Save',        icon: <DatabaseIcon fontSize="small" /> },
  { id: 'indexing',    label: 'Index',        icon: <IndexIcon fontSize="small" /> },
];

interface UploadPipelineProgressProps {
  steps: PipelineStep[];
}

/**
 * Visual pipeline progress indicator for outfit uploads.
 * Shows a horizontal row of circled icons connected by lines,
 * with labels underneath each icon. Icons highlight, pulse, and
 * show checkmarks/errors as each step progresses.
 */
const UploadPipelineProgress: React.FC<UploadPipelineProgressProps> = ({ steps }) => {
  const theme = useTheme();

  const getColor = (status: PipelineStepStatus) => {
    switch (status) {
      case 'done':    return theme.palette.success.main;
      case 'active':  return theme.palette.primary.main;
      case 'error':   return theme.palette.error.main;
      default:        return theme.palette.text.disabled;
    }
  };

  const getBg = (status: PipelineStepStatus) => {
    if (status === 'active') return `${theme.palette.primary.main}18`;
    if (status === 'done')   return `${theme.palette.success.main}14`;
    if (status === 'error')  return `${theme.palette.error.main}14`;
    return 'transparent';
  };

  const getOpacity = (status: PipelineStepStatus) =>
    status === 'pending' ? 0.35 : 1;

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0 }}>
      {steps.map((step, idx) => (
        <React.Fragment key={step.id}>
          {/* Step: icon circle + label */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 56 }}>
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 38,
                height: 38,
                borderRadius: '50%',
                border: `2px solid ${getColor(step.status)}`,
                backgroundColor: getBg(step.status),
                color: getColor(step.status),
                opacity: getOpacity(step.status),
                transition: 'all 0.3s ease',
                animation: step.status === 'active' ? 'pipelinePulse 1.5s ease-in-out infinite' : 'none',
                '@keyframes pipelinePulse': {
                  '0%, 100%': { boxShadow: `0 0 0 0 ${theme.palette.primary.main}44` },
                  '50%': { boxShadow: `0 0 0 6px ${theme.palette.primary.main}22` },
                },
              }}
              title={`${step.label}: ${step.status}`}
            >
              {step.icon}
              {/* Small overlay badge for done/error */}
              {step.status === 'done' && (
                <DoneIcon sx={{ fontSize: 12, position: 'absolute', bottom: -3, right: -3, color: theme.palette.success.main, backgroundColor: theme.palette.background.paper, borderRadius: '50%' }} />
              )}
              {step.status === 'error' && (
                <ErrorIcon sx={{ fontSize: 12, position: 'absolute', bottom: -3, right: -3, color: theme.palette.error.main, backgroundColor: theme.palette.background.paper, borderRadius: '50%' }} />
              )}
            </Box>
            {/* Label */}
            <Typography
              variant="caption"
              sx={{
                mt: 0.5,
                fontSize: '0.68rem',
                fontWeight: step.status === 'active' ? 600 : 400,
                color: getColor(step.status),
                opacity: getOpacity(step.status),
                textAlign: 'center',
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
              }}
            >
              {step.label}
            </Typography>
          </Box>

          {/* Connector line between steps */}
          {idx < steps.length - 1 && (
            <Box
              sx={{
                flex: '1 1 0',
                minWidth: 12,
                maxWidth: 32,
                height: 2,
                mt: '18px', // vertically center with the 38px icon circle
                backgroundColor: steps[idx + 1].status !== 'pending'
                  ? theme.palette.success.main
                  : theme.palette.divider,
                opacity: steps[idx + 1].status !== 'pending' ? 0.7 : 0.3,
                transition: 'background-color 0.3s ease, opacity 0.3s ease',
                borderRadius: 1,
              }}
            />
          )}
        </React.Fragment>
      ))}
    </Box>
  );
};

export default UploadPipelineProgress;
